/**
 * Legal Production KPIs (KAN-36 / KAN-51 / KAN-76 / KAN-77 / KAN-78).
 *
 * Every formula here is the one written down in `docs/kpi-definitions.md`, and
 * that document is the authority — KAN-75 requires the definitions and the code
 * that implements them to live together so they cannot drift. Read it before
 * changing anything in this file.
 *
 * The property this module exists to protect: **a rate is never stored, only
 * derived from its two components**. KAN-77 asks for the numerator and
 * denominator to be kept and shown, because a bare percentage cannot be argued
 * with and a percentage nobody can argue with is one nobody trusts. Deriving
 * the rate here rather than storing a third number means the three can never
 * disagree.
 */
/**
 * The definitions a stored row was computed under.
 *
 * Bump this when an answer in `docs/kpi-definitions.md` changes, so it is
 * possible to say which rows mean which thing. Rows are not retro-fitted: a
 * table whose meaning silently changed on a deploy date somebody has to
 * remember is worse than one carrying two versions honestly.
 */
export const KPI_FORMULA_VERSION = 'v1';
export function rate(numerator, denominator) {
    return {
        numerator,
        denominator,
        /**
         * Null on a zero denominator, and the guard is here rather than at each
         * call site so there is exactly one place it can be forgotten.
         */
        bps: denominator === 0 ? null : Math.round((numerator / denominator) * 10_000),
    };
}
/**
 * The comparison a trend point is read against.
 *
 * Three, because they answer different questions and a screen offering one
 * implies it is the only one worth asking. Month over month catches a change;
 * the same month a year earlier controls for seasonality, which matters in a
 * family-law practice where filings cluster; the rolling average is what a
 * trend line is actually about.
 */
export const KPI_COMPARISONS = ['month_over_month', 'year_over_year', 'rolling_12'];
export const KPI_COMPARISON_LABELS = {
    month_over_month: 'vs. last month',
    year_over_year: 'vs. the same month last year',
    rolling_12: 'vs. the rolling 12-month average',
};
/**
 * The maximum span a trend request may ask for.
 *
 * KAN-78 requires the endpoint to be bounded. Twenty-four months is twice the
 * twelve the ticket asks to be renderable, which leaves room for the
 * year-over-year comparison to have something to compare against.
 */
export const KPI_TREND_MAX_MONTHS = 24;
export const REPORT_DRAFT_STATES = [
    'draft',
    'in_review',
    'approved',
    'sent',
    'discarded',
];
export const REPORT_DRAFT_STATE_LABELS = {
    draft: 'Draft — not sent',
    in_review: 'In review — not sent',
    approved: 'Approved — not yet sent',
    sent: 'Sent',
    discarded: 'Discarded',
};
/**
 * Which states a draft may move to from each state.
 *
 * `sent` is reachable **only from `approved`**, and this table is the shared
 * statement of that. KAN-80's whole point is that no scheduled job, retry
 * handler or webhook may transition straight to `sent`; expressing the machine
 * as data rather than as branches in a controller is what makes the property
 * checkable by reading six lines.
 */
export const REPORT_DRAFT_TRANSITIONS = {
    draft: ['in_review', 'discarded'],
    in_review: ['draft', 'approved', 'discarded'],
    approved: ['sent', 'in_review', 'discarded'],
    /** Terminal. A sent email cannot be unsent, and pretending otherwise helps nobody. */
    sent: [],
    /** Terminal, and deliberately: a discarded draft must never become sendable. */
    discarded: [],
};
export function canTransition(from, to) {
    return REPORT_DRAFT_TRANSITIONS[from].includes(to);
}
//# sourceMappingURL=kpi.js.map